import { useCallback, useMemo, useState } from 'react';
import { MultiselectProps } from '@amzn/awsui-components-console';

export interface TextFilteredOptions {
  /** `options` narrowed to those matching the current filter text. */
  readonly filteredOptions: readonly MultiselectProps.Option[];
  /** The filter text currently entered by the user. */
  readonly filteringText: string;
  /** Wire to `onLoadItems` of a `filteringType="manual"` Select/Multiselect. */
  readonly handleFilteringChange: (filteringText: string) => void;
}

/**
 * Client-side substring filtering for a fetched-once option list.
 *
 * Intended for dropdowns that load their full option list up front and then
 * filter locally, so typing never triggers a server call. Matching is
 * case-insensitive and matches anywhere in the label or value (substring, not
 * prefix) — long ids such as `ns-retail-website-checkout` are far easier to find
 * by a distinctive middle segment than by their shared leading token.
 *
 * Returns `options` by reference when no filter text is entered, so consumers
 * do not re-render on an unchanged list.
 */
export function useTextFilteredOptions(
  options: readonly MultiselectProps.Option[],
): TextFilteredOptions {
  const [filteringText, setFilteringText] = useState('');

  const filteredOptions = useMemo(() => {
    if (!filteringText) return options;
    const lower = filteringText.toLowerCase();
    return options.filter(
      (opt) =>
        (opt.label || '').toLowerCase().includes(lower) ||
        (opt.value || '').toLowerCase().includes(lower),
    );
  }, [options, filteringText]);

  const handleFilteringChange = useCallback((text: string) => {
    setFilteringText(text);
  }, []);

  return { filteredOptions, filteringText, handleFilteringChange };
}
